import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { lastValue } from '../../values/readings.ts';
import { FullScreenLayer } from '../../view/full_screen_layer.tsx';
import { useAppForeground, useScreenActive } from '../../view/screen_activity.ts';
import { TILE_FONT_FAMILY } from '../../view/theme.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { imageAddressOf, readsSource, type ImageTile } from './image_tile.ts';

/** A loaded picture: its address, and whether it is wider than high. */
interface Shown {
    address: string;
    isLandscape: boolean;
}

/**
 * The content area of an `image` tile (docs/specs/image-tile.md): the source's picture, filling the
 * tile or whole (IM-5). A new picture replaces the one shown once it has loaded, so it does not
 * flash; one that cannot be loaded leaves the one before, and the tile says so (IM-7). A tap shows
 * it full screen (IM-6); in edit mode the edit layer takes the taps.
 */
export function ImageTileView({ tile }: { tile: ImageTile }) : ReactNode {
    const environment = useTileEnvironment();
    const value = lastValue(environment.useReadings(tile, readsSource(tile) ? tile.options.source : undefined));
    const address = imageAddressOf(value);
    const screenShown = useScreenActive();
    const foreground = useAppForeground();
    const active = screenShown && foreground;
    const [shown, setShown] = useState<Shown>();
    const [failed, setFailed] = useState<string>();
    const [isMaximized, setMaximized] = useState(false);
    const close = useCallback(() => setMaximized(false), []);

    useEffect(() => {
        if (!address) {
            return undefined;
        }

        let isCurrent = true;
        const image = new Image();

        image.onload = () => {
            if (isCurrent) {
                setShown({ address, isLandscape: image.naturalWidth >= image.naturalHeight });
                setFailed(undefined);
            }
        };
        image.onerror = () => {
            if (isCurrent) {
                setFailed(address);
            }
        };
        image.src = address;

        return () => {
            isCurrent = false;
        };
    }, [address]);

    // Off screen or in the background, full screen closes.
    useEffect(() => {
        if (!active) {
            setMaximized(false);
        }
    }, [active]);

    const note = value !== null && !address ? 'Not a picture' : failed === address && address ? 'The picture could not be loaded' : shown ? undefined : 'No picture';
    const label = tile.label ?? 'Picture';

    return (
        <>
            <div
                style={{ position: 'absolute', inset: 0, overflow: 'hidden', cursor: shown ? 'zoom-in' : undefined }}
                onClick={
                    shown
                        ? (event) => {
                            // Before it reaches the page's body, where React would take it for a tap on the layer.
                            event.stopPropagation();
                            setMaximized(true);
                        }
                        : undefined
                }
            >
                {shown ? <img src={shown.address} alt={label} draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: tile.options.fit }} /> : null}
                {note ? (
                    <div
                        style={{
                            position: 'absolute',
                            ...(shown ? { left: 4, bottom: 4, padding: '2px 6px', borderRadius: 4, background: 'rgba(0, 0, 0, 0.6)' } : { inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
                            color: 'rgba(255, 255, 255, 0.6)',
                            font: `13px/1.2 ${TILE_FONT_FAMILY}`,
                            textAlign: 'center',
                        }}
                    >
                        {note}
                    </div>
                ) : null}
            </div>
            {isMaximized && shown ? (
                <FullScreenLayer label={label} isLandscape={shown.isLandscape} onClose={close}>
                    <img src={shown.address} alt={label} draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }} />
                </FullScreenLayer>
            ) : null}
        </>
    );
}
